import { useMemo } from 'react';

import { createSearchQuery } from '@givebacks/api/query';
import {
  ShowEventResponse,
  useDeleteEventDate,
  useListTickets,
  useShowEventDate,
} from '@givebacks/api/store';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';

interface DeleteEventDateProps extends ModalProps {
  causeId: string;
  eventId?: string;
  dateId?: string;
  onSuccess?: () => void;
}

const selectCartItemCount = (data: ShowEventResponse) => data.event_date?.cart_item_count;

const DeleteEventDateModalContent = ({
  causeId,
  eventId,
  dateId,
  onClose,
  onSuccess,
}: Partial<DeleteEventDateProps>) => {
  const navigate = useNavigate();

  const { data: cartItemCount, isPending: isLoadingEventDate } = useShowEventDate({
    causeId,
    eventId,
    dateId,
    select: selectCartItemCount,
  });

  const { data: seasonTickets, isPending: isLoadingSeasonTickets } = useListTickets({
    eventId,
    causeId,
    ticketsType: 'season_passes',
    queryOptions: {
      searches: {
        deleted: { value: false },
      },
    },
  });
  /** Check if the event date is included in any season tickets */
  const includedInSeasonTickets = useMemo(
    () =>
      seasonTickets?.store_items?.some(
        (seasonTicket) =>
          seasonTicket.event_dates_ids.includes('*') ||
          (dateId && seasonTicket.event_dates_ids.includes(dateId))
      ),
    [seasonTickets?.store_items, dateId]
  );

  const { mutate: deleteEventDate } = useDeleteEventDate();

  const isAtLeastOneTicketSold = useMemo(() => {
    if (!cartItemCount) return false;
    return cartItemCount > 0;
  }, [cartItemCount]);

  const ticketsSoldWarning = useMemo(() => {
    if (cartItemCount === 1) {
      return '1 ticket has already been sold.';
    }
    return `${cartItemCount} tickets have already been sold.`;
  }, [cartItemCount]);

  const infoText = useMemo(() => {
    if (isAtLeastOneTicketSold) {
      return 'Go to the sales history to review and issue refunds.';
    } else if (includedInSeasonTickets) {
      return 'This date is included in a season ticket. Any tickets previously sold will become invalid.';
    } else {
      return 'No sold tickets are currently associated with this date.';
    }
  }, [includedInSeasonTickets, isAtLeastOneTicketSold]);

  const handleDeleteEventDate = () => {
    if (!causeId || !eventId || !dateId) return;
    deleteEventDate(
      { causeId, eventId, dateId },
      {
        onSuccess() {
          notifications.show({ message: 'Event date deleted.', variant: 'success' });
          onSuccess?.();
        },
        onError(e) {
          notifications.show({ message: getErrorMessage_v2(e), variant: 'error' });
        },
      }
    );
  };

  const handleViewEventDateSalesHistory = () => {
    if (!causeId || !eventId || !dateId) return;
    const { queryString } = createSearchQuery({
      searches: {
        'event_dates.uuid': { value: dateId },
      },
    });
    navigate(
      `/ticketed-events/dashboard/event-calendar/${causeId}/campaign-management/${eventId}?${queryString}`
    );
  };

  if (isLoadingEventDate || isLoadingSeasonTickets)
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  return (
    <Stack gap="lg">
      {isAtLeastOneTicketSold && <Alert type="warning" title={ticketsSoldWarning} />}
      <Stack gap="xs">
        <Title order={4}>
          {isAtLeastOneTicketSold
            ? 'To delete this event date, you’ll need to refund all tickets first.'
            : 'Are you sure you want to delete this event date?'}
        </Title>
        <Text fz="sm" c="gray">
          {infoText}
        </Text>
      </Stack>
      <Group justify="flex-end">
        <Button size="md" variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        {isAtLeastOneTicketSold ? (
          <Button size="md" onClick={handleViewEventDateSalesHistory}>
            View Sales History
          </Button>
        ) : (
          <Button size="md" color="cerise" onClick={handleDeleteEventDate}>
            Delete Event Date
          </Button>
        )}
      </Group>
    </Stack>
  );
};

const DeleteEventDateModal = ({
  causeId,
  eventId,
  dateId,
  opened,
  onClose,
  onSuccess,
}: DeleteEventDateProps) => {
  return (
    <Modal opened={opened} onClose={onClose} size="lg" title="Delete Event Date">
      <DeleteEventDateModalContent
        causeId={causeId}
        eventId={eventId}
        dateId={dateId}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
};

export { DeleteEventDateModal };
